<template>
  <div class="page page-product">
    <div class="page__wrap" data-animate="page-wrap" v-if="product">
      <!-- Header -->
      <PagesProductHeader
        :product="product"
        :navigationFrom="props.navigationFrom"
      />
      <div data-animate="product-content">
        <!-- Editorial -->
        <LazyEditorialList
          class="m-top-3 m-bot-3"
          v-if="product?.editorialContent?.length"
          :content="product.editorialContent"
        />
        <!-- Specs -->
        <LazyPagesProductSpecifications
          class="m-top-3 m-bot-3"
          :product="product"
        />
        <!-- Download -->
        <LazyPagesProductDownload :product="product" />
        <!-- Finishings -->
        <LazyPagesFinishingsListProduct
          v-if="
            product?.relatedFinishing?.length ||
            product?.relatedInternalFinishing?.length ||
            product?.relatedWireFinishing?.length ||
            product?.relatedBaseFinishing?.length
          "
          class="m-top-3"
          :finishings="product.relatedFinishing"
          :internalFinishings="product.relatedInternalFinishing"
          :wireFinishings="product.relatedWireFinishing"
          :baseFinishings="product.relatedBaseFinishing"
          :class="{
            'm-bot-3': siblings?.length || product?.productRecommended?.length,
          }"
        />

        <!-- Siblings -->
        <LazyPagesProductProductsSiblings
          v-if="siblings?.length"
          class="m-top-3"
          :class="{
            'p-bot-3': product?.productRecommended?.length,
          }"
          :parent="product.parent"
          :siblings="siblings"
        />
        <!-- Recommended -->
        <LazyPagesProductProductsRecommended
          v-if="product?.productRecommended?.length"
          :products="product.productRecommended"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { useCraftStore } from '@/store/craft.js'

import queries from '@/graphql/craft/queries/index.js'
import {
  onBeforeEnter,
  onEnter,
  onLeave,
} from '~/assets/js/page-transitions/product.js'

definePageMeta({
  layout: 'default',
  pageTransition: {
    onBeforeEnter: (el) => {
      onBeforeEnter(el)
    },
    onEnter: async (el, done) => {
      const tl = onEnter(el, done)
      await tl.delay(0.75).play()
      done()
    },
    onLeave: async (el, done) => {
      const tl = onLeave(el)
      await tl.play()
      window.lenis.scrollTo(0, { immediate: true })
      done()
    },
  },
})

// Dall'app.vue prendo il navigation guard
const props = defineProps({
  navigationFrom: Object,
})

const { t } = useI18n()

// Fetch Craft
const { configuration } = useCraftStore()

const route = useRoute()

const query = queries.products.getProduct
const variables = computed(() => ({
  site: configuration.site,
  marketId: configuration.marketId,
  productSlug: route.params.slug,
}))
const { data } = await useGraphql(query, variables)

if (!data.value.productsEntries?.[0]) {
  throw createError({ statusCode: 404 })
}

const product = computed(() => data.value.productsEntries?.[0])

const siblings = computed(() => {
  return data.value.productsEntries?.[0]?.parent?.children
    .filter((product) => product.slug !== route.params.slug)
    .map((product) => ({
      title: product.title,
      cover: product.coverPortrait?.[0] || product.cover?.[0],
      route: {
        name: 'product-slug',
        params: { slug: product.slug },
      },
    }))
})

const seo = computed(() => data.value.productsEntries?.[0]?.seo || {})
const localeParams = computed(
  () => data.value?.productsEntries?.[0]?.localized || {}
)

// Seo
useHead({
  titleTemplate: '%s - %product',
  templateParams: {
    product: t('bucket.seoTitleProduct'),
  },
})
useSeo({
  ...seo.value,
  localeParams: useHelpers().generateSeoLocaleParams(localeParams, 'slug'),
  meta: [
    {
      name: 'description',
      content:
        seo.value.meta?.description ||
        t('bucket.seoProductDescriptionPlaceholder', {
          product: product.value?.title,
        }),
    },
  ],
  market: product?.value?.market,
})
</script>
